import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin, Search, Clock, Package, X } from 'lucide-react';
import api from '../lib/api';

const LockerPickerDialog = ({ isOpen, onClose, onSelectLocker, selectedLockerId }) => {
  const [county, setCounty] = useState('');
  const [city, setCity] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [lockers, setLockers] = useState([]);
  const [filteredLockers, setFilteredLockers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableCounties, setAvailableCounties] = useState([]);
  const [selectedLocker, setSelectedLocker] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (isOpen) {
      loadLockers();
    }
  }, [isOpen]);

  useEffect(() => {
    filterLockers();
  }, [lockers, county, city, searchQuery]);

  // Initialize map when dialog opens and lockers are loaded
  useEffect(() => {
    if (isOpen && filteredLockers.length > 0 && mapRef.current) {
      initMap();
    }
    return () => {
      // Cleanup map on unmount
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isOpen, filteredLockers]);

  const initMap = () => {
    // Load Leaflet CSS if not already loaded
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Load Leaflet JS if not already loaded
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => createMap();
      document.head.appendChild(script);
    } else {
      createMap();
    }
  };

  const createMap = () => {
    if (!mapRef.current || !window.L) return;
    
    // Remove existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
    }

    // Find center point from filtered lockers with coordinates
    const lockersWithCoords = filteredLockers.filter(l => l.lat && l.lng);
    let center = [45.9432, 24.9668]; // Romania center
    let zoom = 7;

    if (lockersWithCoords.length > 0) {
      const avgLat = lockersWithCoords.reduce((sum, l) => sum + parseFloat(l.lat), 0) / lockersWithCoords.length;
      const avgLng = lockersWithCoords.reduce((sum, l) => sum + parseFloat(l.lng), 0) / lockersWithCoords.length;
      center = [avgLat, avgLng];
      zoom = lockersWithCoords.length === 1 ? 15 : (lockersWithCoords.length < 10 ? 12 : 10);
    }

    // Create map
    const map = window.L.map(mapRef.current).setView(center, zoom);
    mapInstanceRef.current = map;

    // Add tile layer (OpenStreetMap)
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap'
    }).addTo(map);

    // Clear existing markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // Add markers for lockers
    lockersWithCoords.forEach(locker => {
      const isSelected = selectedLockerId === locker.id;
      
      // Custom icon
      const icon = window.L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          background: ${isSelected ? '#22c55e' : '#3b82f6'};
          width: 32px;
          height: 32px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        ">
          <span style="transform: rotate(45deg); color: white; font-size: 14px;">📦</span>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });

      const marker = window.L.marker([parseFloat(locker.lat), parseFloat(locker.lng)], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="min-width: 200px;">
            <strong style="color: #3b82f6;">${locker.name}</strong><br/>
            <span style="font-size: 12px; color: #666;">${locker.address}</span><br/>
            <span style="font-size: 11px; color: #888;">${locker.city}, ${locker.county}</span><br/>
            <button onclick="window.selectLocker('${locker.id}')" style="
              margin-top: 8px;
              padding: 6px 12px;
              background: #22c55e;
              color: white;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              font-size: 12px;
              width: 100%;
            ">Selectează acest locker</button>
          </div>
        `);
      
      markersRef.current.push(marker);
    });

    // Add global function for popup button
    window.selectLocker = (lockerId) => {
      const locker = lockers.find(l => l.id === lockerId);
      if (locker) {
        handleSelectLocker(locker);
      }
    };

    // Fit bounds if multiple lockers
    if (lockersWithCoords.length > 1) {
      const bounds = window.L.latLngBounds(lockersWithCoords.map(l => [parseFloat(l.lat), parseFloat(l.lng)]));
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  };

  const loadLockers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sameday/lockers');
      const lockersData = response.data || [];
      setLockers(lockersData);
      
      // Extract unique counties from API data
      const counties = [...new Set(lockersData.map(l => l.county).filter(Boolean))].sort();
      setAvailableCounties(counties);
      console.log('[LOCKER PICKER] Loaded', lockersData.length, 'lockers from', counties.length, 'counties');
    } catch (error) {
      console.error('Error loading lockers:', error);
      setLockers([]);
      setAvailableCounties([]);
    } finally {
      setLoading(false);
    }
  };

  const filterLockers = () => {
    let filtered = [...lockers];

    // If search query is provided, search in ALL lockers (ignore county/city filters)
    if (searchQuery && searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase().trim();
      filtered = lockers.filter(l =>
        l.name?.toLowerCase().includes(query) ||
        l.address?.toLowerCase().includes(query) ||
        l.city?.toLowerCase().includes(query) ||
        l.county?.toLowerCase().includes(query)
      );
    } else {
      // Apply county/city filters only when not searching
      if (county) {
        filtered = filtered.filter(l => l.county === county);
      }
      if (city) {
        filtered = filtered.filter(l => l.city === city);
      }
    }

    setFilteredLockers(filtered);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Clear county/city filters when searching
    if (value.trim().length > 0) {
      setCounty('');
      setCity('');
    }
  };

  const handleCountyChange = (val) => {
    setCounty(val);
    setCity('');
    setSearchQuery(''); // Clear search when selecting county
  };

  const handleCityChange = (val) => {
    setCity(val);
    setSearchQuery(''); // Clear search when selecting city
  };

  const clearFilters = () => {
    setCounty('');
    setCity('');
    setSearchQuery('');
  };

  const getCities = () => {
    if (!county) return [];
    return [...new Set(lockers.filter(l => l.county === county).map(l => l.city).filter(Boolean))].sort();
  };

  const handleSelectLocker = (locker) => {
    setSelectedLocker(locker);
    onSelectLocker(locker);
    onClose();
  };

  const focusOnLocker = (locker) => {
    if (mapInstanceRef.current && locker.lat && locker.lng) {
      mapInstanceRef.current.setView([parseFloat(locker.lat), parseFloat(locker.lng)], 16);
      // Open popup for this locker
      markersRef.current.forEach(marker => {
        const latLng = marker.getLatLng();
        if (Math.abs(latLng.lat - parseFloat(locker.lat)) < 0.0001 && 
            Math.abs(latLng.lng - parseFloat(locker.lng)) < 0.0001) {
          marker.openPopup();
        }
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] max-h-[90vh] overflow-hidden p-0 [&>button]:hidden">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="flex items-center justify-between">
            <span className="text-xl font-bold">Alege punct de ridicare</span>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 h-[calc(90vh-80px)]">
          {/* Left Side - Filters & List (2 columns) */}
          <div className="lg:col-span-2 p-6 border-r border-gray-200 overflow-y-auto">
            {/* Search - full width, searches everywhere */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Caută locker după nume, adresă sau localitate..."
                className="pl-10 pr-10 rounded-lg"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* County & City Dropdowns */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Select value={county || "_all_"} onValueChange={(val) => handleCountyChange(val === "_all_" ? "" : val)}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder="Toate județele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all_">Toate județele</SelectItem>
                  {availableCounties.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={city || "_all_"} onValueChange={(val) => handleCityChange(val === "_all_" ? "" : val)} disabled={!county}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder={county ? "Toate localitățile" : "Selectează județ"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all_">Toate localitățile</SelectItem>
                  {getCities().map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Active filters indicator */}
            {(county || city || searchQuery) && (
              <div className="flex items-center justify-between mb-4 p-2 bg-blue-50 rounded-lg">
                <span className="text-sm text-blue-700">
                  {searchQuery ? `Căutare: "${searchQuery}"` : `${county}${city ? ` > ${city}` : ''}`}
                </span>
                <button 
                  onClick={clearFilters}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Resetează filtrele
                </button>
              </div>
            )}

            {/* Delivery Info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex items-center space-x-3 mb-3">
                <Package className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-semibold text-sm">Livrare în easybox:</p>
                  <p className="text-xs text-green-600">Gratuit • Disponibil în 1-2 zile lucrătoare</p>
                </div>
              </div>
            </div>

            {/* Lockers List */}
            <div className="space-y-2">
              <h3 className="font-semibold text-sm mb-3 flex items-center justify-between">
                <span>Puncte de ridicare ({filteredLockers.length})</span>
                {filteredLockers.length > 100 && (
                  <span className="text-xs text-gray-500 font-normal">Folosește filtrele pentru rezultate mai precise</span>
                )}
              </h3>
              
              <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500">Se încarcă lockerele...</p>
                  </div>
                ) : filteredLockers.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">
                    {searchQuery ? 'Nu s-au găsit lockere pentru căutarea ta' : 'Nu sunt lockere în această zonă'}
                  </p>
                ) : (
                  filteredLockers.slice(0, 100).map((locker) => (
                    <div
                      key={locker.id}
                      onClick={() => handleSelectLocker(locker)}
                      onMouseEnter={() => focusOnLocker(locker)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedLockerId === locker.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-white hover:border-green-500 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-blue-600">{locker.name}</p>
                          <p className="text-xs text-gray-600 mt-1">{locker.address}</p>
                          <p className="text-xs text-gray-500">{locker.city}, Jud. {locker.county}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {locker.lat && locker.lng && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); focusOnLocker(locker); }}
                              className="text-blue-500 hover:text-blue-700 p-1"
                              title="Vezi pe hartă"
                            >
                              <MapPin className="h-4 w-4" />
                            </button>
                          )}
                          {selectedLockerId === locker.id && (
                            <div className="bg-green-500 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                              ✓
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {filteredLockers.length > 100 && (
                  <p className="text-center text-sm text-gray-500 py-2">
                    Se afișează primele 100 rezultate. Folosește căutarea sau filtrele pentru a găsi mai ușor.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Map (3 columns - wider) */}
          <div className="lg:col-span-3 relative bg-gray-100">
            <div ref={mapRef} className="absolute inset-0 z-0" />
            {(!filteredLockers.some(l => l.lat && l.lng)) && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="text-center p-8">
                  <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-semibold mb-2">Hartă Lockere</p>
                  <p className="text-sm text-gray-500">
                    Selectează un județ pentru a vedea lockerele pe hartă
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LockerPickerDialog;
